// One number/time vocabulary for the whole Markets tab.
//
// Money: sign first, then $, two decimals — "+$0.21", "-$14.40", "$0.00".
// Times: the operator's zone (from /api/system/pulse → operator_tz), always
// labelled ("4:17 PM CT"). Backend timestamps without an offset are UTC.

export const ACTIVE_SCOPE = "4 active accounts";

export function money(v, { dec = 2, sign = true, compact = false } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  let body;
  if (compact && abs >= 10000) {
    body = `${(abs / 1000).toLocaleString("en-US", { maximumFractionDigits: 1 })}k`;
  } else {
    body = abs.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
  }
  const rounded = Number(abs.toFixed(dec));
  if (rounded === 0) return `$${body}`;
  if (!sign) return `${n < 0 ? "-" : ""}$${body}`;
  return `${n < 0 ? "-" : "+"}$${body}`;
}

export const tone = (v) => (Number(v) > 0 ? "is-up" : Number(v) < 0 ? "is-down" : "");

export function pct(v, { dec = 1, sign = false } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const s = Math.abs(n).toFixed(dec);
  if (Number(s) === 0) return `${s}%`;
  return `${n < 0 ? "-" : sign ? "+" : ""}${s}%`;
}

export function price(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "—";
  const a = Math.abs(n);
  const dec = a >= 1000 ? 1 : a >= 10 ? 2 : a >= 1 ? 3 : a >= 0.01 ? 5 : 7;
  return n.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

export const count = (n) => Number(n || 0).toLocaleString("en-US");

// "2026-10-08 00:57:40.25" and "2026-10-08T00:57:40" are UTC without a marker.
export function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === "number") return Number.isFinite(v) ? new Date(v) : null;
  let s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    s = `${s.replace(" ", "T")}Z`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function tzLabel(date, tz) {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(date).find((p) => p.type === "timeZoneName")?.value || "";
    return /^[ECMPAH][SD]T$/.test(part) ? `${part[0]}T` : part;
  } catch {
    return "UTC";
  }
}

export function fmtTime(v, tz, { date = false, seconds = false } = {}) {
  const d = toDate(v);
  if (!d) return "—";
  try {
    const opts = { timeZone: tz, hour: "numeric", minute: "2-digit" };
    if (seconds) opts.second = "2-digit";
    if (date) { opts.month = "short"; opts.day = "numeric"; }
    return `${new Intl.DateTimeFormat("en-US", opts).format(d)} ${tzLabel(d, tz)}`;
  } catch {
    return d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
  }
}

// "YYYY-MM-DD" of a moment in the operator's zone.
export function dayKey(v, tz) {
  const d = toDate(v) || new Date();
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export function fmtDay(key, tz) {
  // key is a calendar date already in `tz`; render it without shifting.
  const [y, m, d] = String(key).split("-").map(Number);
  if (!y) return key;
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" }).format(dt);
}

export function ago(v) {
  const d = toDate(v);
  if (!d) return "—";
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  return agoSec(s);
}

export function agoSec(s) {
  if (s == null || !Number.isFinite(Number(s))) return "—";
  s = Number(s);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export const base = (sym) => String(sym || "").replace("/USDT", "").replace(":USDT", "");

// $ lost if the stop is hit (falls back to margin when there is no stop).
export function riskAtStop(p) {
  const entry = Number(p.entry_price);
  const stop = Number(p.stop_loss ?? p.stop_price);
  const size = Math.abs(Number(p.position_size_usd ?? 0)) || Math.abs(Number(p.margin || 0) * Number(p.leverage || 1));
  if (entry > 0 && stop > 0 && size > 0) return (Math.abs(entry - stop) / entry) * size;
  return Number(p.margin || 0);
}

export function notional(p) {
  const explicit = Number(p.position_size_usd ?? p.notional ?? p.size_usd);
  if (Number.isFinite(explicit) && explicit) return Math.abs(explicit);
  return Math.abs(Number(p.margin || 0) * Number(p.leverage || 1));
}

export function tradePnl(t) {
  return Number(t.realized_pnl ?? t.pnl_usd ?? t.pnl ?? 0) || 0;
}

export function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [columns.map((c) => esc(c.label)).join(","),
    ...rows.map((r) => columns.map((c) => esc(c.csv ? c.csv(r) : r[c.key])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
